import {
  getGetSuperAdminFranchisesFranchiseIdHardwareQueryKey,
  getGetSuperAdminFranchisesFranchiseIdModuleBillingQueryKey,
  useDeleteSuperAdminHardwareId,
  useDeleteSuperAdminModuleBillingId,
  useGetSuperAdminFranchisesFranchiseIdHardware,
  useGetSuperAdminFranchisesFranchiseIdModuleBilling,
  useGetSuperAdminModulesCatalog,
  usePatchSuperAdminHardwareId,
  usePatchSuperAdminModuleBillingId,
  usePostSuperAdminFranchisesFranchiseIdHardware,
  usePostSuperAdminFranchisesFranchiseIdModuleBilling,
} from '@agos/api-client/platform'
import { T, formatFixed, tt } from '@agos/shared'
import { ConfirmDialog, toast, ReadFailed, Spinner } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Monitor, Package, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  availableModules,
  billingItemPatch,
  billingItemProblem,
  billingItemProblemMessage,
  catalogOf,
  draftOfHardware,
  draftOfModule,
  hardwareRowsOf,
  moduleLabel,
  moduleRowsOf,
  monthlyTotals,
  rentalPeriodOf,
  type BillingItemDraft,
  type HardwareItem,
  type ModuleChargeItem,
} from './franchise-billing-items'

/**
 * What a franchise is charged on top of its package - `SAAS.md` §3.37.
 *
 * **Eight endpoints had no caller, the two reads included**, so the Tower could neither show nor
 * change 45,00 EUR a month that `BillingRunner` was already putting on franchise 1's invoice. Legacy
 * has both sections in full on its own `FranchiseDetail`, so this is a missed port.
 *
 * **Its own file rather than 300 more lines on a page of 866**, and the same split legacy uses. The
 * page owns the tier, the overrides and the retirement; this owns the charges.
 *
 * **Errors toast rather than carry §3.35's banner.** That entry fixed four screens in
 * `apps/super-admin/src/pages` which had no `onError` at all; `FranchiseDetailPage` already reports a
 * failed save with a destructive `toast`, and one idiom per page is worth more than consistency with
 * a different page.
 */

const FIELD =
  'w-full min-h-[38px] px-3 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

function euro(cents: number): string {
  return `${formatFixed(cents / 100, 2)} €`
}

function refused(what: string) {
  toast({ variant: 'destructive', title: what, description: '' })
}

/** One money input, labelled - the two appear four times between the two modals. */
/**
 * A day, as `<input type="date">` holds it - §3.120.
 *
 * Beside `MoneyField` and shaped like it, because the two sit in the same grid on the same modal and a
 * date that looked like a different kind of control would read as a different kind of field.
 */
function DateField({
  label,
  hint,
  value,
  onChange,
  testId,
}: {
  label: string
  hint: string
  value: string
  onChange: (next: string) => void
  testId: string
}) {
  return (
    <label className="block space-y-1">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        data-testid={testId}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={FIELD}
      />
      <span className="block text-[10px] text-muted-foreground">{hint}</span>
    </label>
  )
}

/**
 * What the table says about one rental - §3.120.
 *
 * <p>The three states come from `rentalPeriodOf` so they can be asserted apart from the markup; this
 * only words them. *Vrátené* rather than a date range, because the question an operator is asking of
 * this column is whether the device is still out.
 */
function HardwarePeriod({ row }: { row: HardwareItem }) {
  const period = rentalPeriodOf(row)
  if (period.kind === 'unknown') {
    /* `starts_at` is NOT NULL, so this means the field did not reach the screen rather than that the
       device has no start. Said rather than left blank - a blank cell reads as *no period*, which
       would be a claim about the rental instead of about the read. */
    return (
      <span className="text-muted-foreground" data-hardware-period-unknown>
        <T>Neznáme</T>
      </span>
    )
  }
  if (period.kind === 'returned') {
    return (
      <span className="text-muted-foreground" data-hardware-returned={period.returnedOn}>
        <T>Vrátené</T> {period.returnedOn}
      </span>
    )
  }
  return (
    <span data-hardware-out={period.since}>
      <T>Od</T> {period.since}
    </span>
  )
}

function MoneyField({
  label,
  hint,
  value,
  onChange,
  testId,
}: {
  label: string
  hint: string
  value: string
  onChange: (next: string) => void
  testId: string
}) {
  return (
    <label className="block space-y-1">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        data-testid={testId}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={FIELD}
      />
      <span className="block text-[10px] text-muted-foreground">{hint}</span>
    </label>
  )
}

function EnabledToggle({
  checked,
  onChange,
  testId,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  testId: string
}) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {/* A switched-off row stays on screen and reaches no invoice - `BillingRunner` filters
       * `enabled = true`. Said here because *deleted* and *not charged* are different answers. */}
      <span>
        <T>Účtovať na mesačnej faktúre</T>
      </span>
    </label>
  )
}

/** The charged/cost/margin line both of legacy's tables leave to the reader's arithmetic. */
function TotalsLine({
  hardware,
  modules,
}: {
  hardware: readonly HardwareItem[]
  modules: readonly ModuleChargeItem[]
}) {
  const totals = useMemo(() => monthlyTotals(hardware, modules), [hardware, modules])
  if (hardware.length === 0 && modules.length === 0) return null

  return (
    <p className="text-[11px] text-muted-foreground" data-billing-totals>
      <T>Mesačne účtované</T>: <strong data-billing-charged>{euro(totals.chargedCents)}</strong>
      {' · '}
      <T>náklad</T> {euro(totals.costCents)}
      {' · '}
      <T>marža</T> {euro(totals.marginCents)}
      {totals.disabled > 0 && (
        <span data-billing-disabled>
          {' · '}
          {totals.disabled} <T>neúčtovaných</T>
        </span>
      )}
    </p>
  )
}

export function FranchiseBillingItemsSections({ franchiseId }: { franchiseId: number }) {
  const queryClient = useQueryClient()
  const hardwareRead = useGetSuperAdminFranchisesFranchiseIdHardware(franchiseId)
  const moduleRead = useGetSuperAdminFranchisesFranchiseIdModuleBilling(franchiseId)
  const catalogRead = useGetSuperAdminModulesCatalog()

  /* FB-808, §3.149 - **an empty list here is a statement about the franchise, and these two are
     stronger than most**: one asserts BYOH, a commercial arrangement, and the other asserts what the
     invoice will contain. Neither may be drawn over a read that failed, and until this entry the word
     `isError` did not appear in this file.
     §3.81 gated every Tower empty state on `!isLoading && !isError` and claimed it for the Tower -
     **its unit was the page**, and this is a section component mounted inside `FranchiseDetailPage`,
     which guards only the franchise *list* read. So these three failures were reported by nothing.
     Per read rather than per panel: the hardware list failing says nothing about the module charges,
     and one banner for the three would replace a working half of the screen with a fault report. */
  const hardware = useMemo(() => hardwareRowsOf(hardwareRead.data), [hardwareRead.data])
  const modules = useMemo(() => moduleRowsOf(moduleRead.data), [moduleRead.data])
  const catalog = useMemo(() => catalogOf(catalogRead.data), [catalogRead.data])

  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: getGetSuperAdminFranchisesFranchiseIdHardwareQueryKey(franchiseId),
    })
    void queryClient.invalidateQueries({
      queryKey: getGetSuperAdminFranchisesFranchiseIdModuleBillingQueryKey(franchiseId),
    })
  }

  const [hardwareEdit, setHardwareEdit] = useState<
    { row: HardwareItem | null; draft: BillingItemDraft } | null
  >(null)
  const [moduleEdit, setModuleEdit] = useState<
    { row: ModuleChargeItem | null; draft: BillingItemDraft } | null
  >(null)
  const [removing, setRemoving] = useState<
    { kind: 'hardware' | 'module'; id: number; label: string } | null
  >(null)

  const closed = () => {
    setHardwareEdit(null)
    setModuleEdit(null)
    refresh()
  }
  const onError = () => refused(tt('Zmenu sa nepodarilo uložiť.'))

  const addHardware = usePostSuperAdminFranchisesFranchiseIdHardware({
    mutation: { onSuccess: closed, onError },
  })
  const patchHardware = usePatchSuperAdminHardwareId({ mutation: { onSuccess: closed, onError } })
  const dropHardware = useDeleteSuperAdminHardwareId({
    mutation: { onSuccess: () => { setRemoving(null); refresh() }, onError },
  })
  const addModule = usePostSuperAdminFranchisesFranchiseIdModuleBilling({
    mutation: { onSuccess: closed, onError },
  })
  const patchModule = usePatchSuperAdminModuleBillingId({ mutation: { onSuccess: closed, onError } })
  const dropModule = useDeleteSuperAdminModuleBillingId({
    mutation: { onSuccess: () => { setRemoving(null); refresh() }, onError },
  })

  const saveHardware = () => {
    if (hardwareEdit === null) return
    const problem = billingItemProblem(hardwareEdit.draft, 'hardware')
    if (problem !== null) {
      refused(billingItemProblemMessage(problem))
      return
    }
    if (hardwareEdit.row === null) {
      addHardware.mutate({
        franchiseId,
        data: {
          description: hardwareEdit.draft.description.trim(),
          monthlyPrice: Number(hardwareEdit.draft.monthlyPrice.replace(',', '.')),
          monthlyCost: Number(hardwareEdit.draft.monthlyCost.replace(',', '.')),
          enabled: hardwareEdit.draft.enabled,
          /* §3.120 - the period the billing run charges from. `draftOfHardware(null)` seeds the
             start with today, which is what the server would have defaulted to anyway; sending it
             means the operator can change it before the first invoice rather than after. An
             unreturned device sends no end at all, which is what a null `ends_at` means. */
          startsAt: hardwareEdit.draft.startsAt,
          ...(hardwareEdit.draft.endsAt !== undefined && hardwareEdit.draft.endsAt !== ''
            ? { endsAt: hardwareEdit.draft.endsAt }
            : {}),
        },
      })
      return
    }
    // **Only what changed** - `updateHardware` builds its `SET` from the keys present, so a
    // whole-row send writes every column including the ones nobody touched (§3.5).
    const patch = billingItemPatch(hardwareEdit.draft, hardwareEdit.row)
    if (Object.keys(patch).length === 0) {
      setHardwareEdit(null)
      return
    }
    patchHardware.mutate({ id: hardwareEdit.row.id, data: patch })
  }

  const saveModule = () => {
    if (moduleEdit === null) return
    const problem = billingItemProblem(moduleEdit.draft, 'module')
    if (problem !== null) {
      refused(billingItemProblemMessage(problem))
      return
    }
    if (moduleEdit.row === null) {
      addModule.mutate({
        franchiseId,
        data: {
          moduleKey: moduleEdit.draft.moduleKey,
          description: moduleEdit.draft.description.trim(),
          monthlyPrice: Number(moduleEdit.draft.monthlyPrice.replace(',', '.')),
          monthlyCost: Number(moduleEdit.draft.monthlyCost.replace(',', '.')),
          enabled: moduleEdit.draft.enabled,
        },
      })
      return
    }
    const patch = billingItemPatch(moduleEdit.draft, moduleEdit.row)
    if (Object.keys(patch).length === 0) {
      setModuleEdit(null)
      return
    }
    patchModule.mutate({ id: moduleEdit.row.id, data: patch })
  }

  const offered = availableModules(catalog, modules, moduleEdit?.row !== null)
  const busy =
    addHardware.isPending ||
    patchHardware.isPending ||
    addModule.isPending ||
    patchModule.isPending

  return (
    <>
      {/* ─── Hardware ──────────────────────────────────────────────────────── */}
      <section className="bg-card border border-border rounded-2xl p-5 space-y-3" data-hardware>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <Monitor className="w-4 h-4 text-primary" />
              <T>Hardvér (prenájom)</T>
            </h2>
            <TotalsLine hardware={hardware} modules={modules} />
          </div>
          <button
            type="button"
            data-hardware-add
            onClick={() => setHardwareEdit({ row: null, draft: draftOfHardware(null) })}
            className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold"
          >
            <Plus className="w-3.5 h-3.5" /> <T>Pridať</T>
          </button>
        </div>

        {hardwareRead.isError && (
          <ReadFailed marker="hardware-error"><T>Zariadenia sa nepodarilo načítať.</T></ReadFailed>
        )}
        {hardwareRead.isLoading && (
          <div className="py-6 flex justify-center" data-hardware-loading>
            <Spinner />
          </div>
        )}
        {hardware.length === 0 ? (
          hardwareRead.isError || hardwareRead.isLoading ? null : (
            <p className="text-xs text-muted-foreground" data-hardware-empty>
              {/* Legacy's own sentence, BYOH and all - it is the operative fact, not decoration.
                  Which is exactly why it may only be said when the read succeeded: on a failure it
                  reports a franchise renting six devices as supplying its own. */}
              <T>Žiadne zariadenia (BYOH - vlastný hardvér).</T>
            </p>
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-hardware-table>
              <thead>
                <tr className="text-muted-foreground text-[10px] uppercase tracking-wider border-b border-border">
                  <th className="text-left py-2">
                    <T>Položka</T>
                  </th>
                  <th className="text-right py-2 px-3">
                    <T>€/mes. (účtujeme)</T>
                  </th>
                  <th className="text-right py-2 px-3">
                    <T>€/mes. (náklad)</T>
                  </th>
                  <th className="text-left py-2 px-3">
                    <T>Obdobie</T>
                  </th>
                  <th className="text-center py-2 px-3">
                    <T>Účtované</T>
                  </th>
                  <th className="text-right py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {hardware.map((row) => (
                  <tr key={row.id} data-hardware-row={String(row.id)}>
                    <td className="py-2">{row.description}</td>
                    <td className="py-2 px-3 text-right tabular-nums" data-hardware-price>
                      {formatFixed(row.monthlyPrice, 2)} €
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                      {formatFixed(row.monthlyCost, 2)} €
                    </td>
                    {/* §3.120 - which devices are still out. Half the point of the entry: an
                        operator who cannot see the period cannot end one, and `Účtované` beside it
                        answers a different question - *bill this at all* rather than *until when*. */}
                    <td className="py-2 px-3 text-[11px]" data-hardware-period-cell={String(row.id)}>
                      <HardwarePeriod row={row} />
                    </td>
                    <td className="py-2 px-3 text-center">{row.enabled ? '✓' : '-'}</td>
                    <td className="py-2">
                      <div className="flex gap-1 justify-end">
                        <button
                          type="button"
                          data-hardware-edit={String(row.id)}
                          onClick={() =>
                            setHardwareEdit({ row, draft: draftOfHardware(row) })
                          }
                          className="min-h-[32px] px-2 rounded-lg border border-border text-[10px] font-semibold"
                        >
                          <T>Upraviť</T>
                        </button>
                        <button
                          type="button"
                          data-hardware-remove={String(row.id)}
                          onClick={() =>
                            setRemoving({ kind: 'hardware', id: row.id, label: row.description })
                          }
                          className="min-h-[32px] px-2 rounded-lg border border-red-500/40 text-red-500 text-[10px] font-semibold"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ─── Module billing ───────────────────────────────────────────────── */}
      <section
        className="bg-card border border-border rounded-2xl p-5 space-y-3"
        data-module-billing
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" />
              <T>Účtovanie modulov</T>
            </h2>
            <p className="text-[11px] text-muted-foreground">
              <T>
                Účtovanie nezávislé od balíčka. Každý účtovaný riadok je jedna položka na mesačnej
                faktúre.
              </T>
            </p>
          </div>
          <button
            type="button"
            data-module-add
            disabled={offered.length === 0}
            onClick={() =>
              setModuleEdit({ row: null, draft: draftOfModule(null, offered[0]?.key ?? '') })
            }
            className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5" /> <T>Pridať modul</T>
          </button>
          {/* **Said on the control it disables.** A disabled *Pridať modul* is legitimate when every
              module is already charged, so `offered.length === 0` cannot tell *nothing left to add*
              from *the catalogue never arrived* - and the operator is looking at the button. */}
          {catalogRead.isError && (
            <p className="text-xs text-red-500" data-module-catalog-error>
              <T>Zoznam modulov sa nepodarilo načítať, preto sa nedá pridať.</T>
            </p>
          )}
        </div>

        {moduleRead.isError && (
          <ReadFailed marker="module-billing-error"><T>Účtované moduly sa nepodarilo načítať.</T></ReadFailed>
        )}
        {moduleRead.isLoading && (
          <div className="py-6 flex justify-center" data-module-billing-loading>
            <Spinner />
          </div>
        )}
        {modules.length === 0 ? (
          moduleRead.isError || moduleRead.isLoading ? null : (
            <p className="text-xs text-muted-foreground" data-module-billing-empty>
              {/* Asserts what the invoice contains, so it waits for a read that actually answered. */}
              <T>
                Žiadne moduly sa neúčtujú jednotlivo. Fakturuje sa cena balíčka, hardvér a API
                volania.
              </T>
            </p>
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-module-billing-table>
              <thead>
                <tr className="text-muted-foreground text-[10px] uppercase tracking-wider border-b border-border">
                  <th className="text-left py-2">
                    <T>Modul</T>
                  </th>
                  <th className="text-left py-2 px-3">
                    <T>Popis na faktúre</T>
                  </th>
                  <th className="text-right py-2 px-3">
                    <T>€/mes. (účtujeme)</T>
                  </th>
                  <th className="text-right py-2 px-3">
                    <T>€/mes. (náklad)</T>
                  </th>
                  <th className="text-center py-2 px-3">
                    <T>Účtované</T>
                  </th>
                  <th className="text-right py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {modules.map((row) => (
                  <tr key={row.id} data-module-row={row.moduleKey}>
                    <td className="py-2">
                      <p className="font-semibold">{moduleLabel(row)}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{row.moduleKey}</p>
                    </td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">
                      {row.description === '' ? '-' : row.description}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums" data-module-price>
                      {formatFixed(row.monthlyPrice, 2)} €
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                      {formatFixed(row.monthlyCost, 2)} €
                    </td>
                    <td className="py-2 px-3 text-center">{row.enabled ? '✓' : '-'}</td>
                    <td className="py-2">
                      <div className="flex gap-1 justify-end">
                        <button
                          type="button"
                          data-module-edit={row.moduleKey}
                          onClick={() =>
                            setModuleEdit({ row, draft: draftOfModule(row, row.moduleKey) })
                          }
                          className="min-h-[32px] px-2 rounded-lg border border-border text-[10px] font-semibold"
                        >
                          <T>Upraviť</T>
                        </button>
                        <button
                          type="button"
                          data-module-remove={row.moduleKey}
                          onClick={() =>
                            setRemoving({ kind: 'module', id: row.id, label: moduleLabel(row) })
                          }
                          className="min-h-[32px] px-2 rounded-lg border border-red-500/40 text-red-500 text-[10px] font-semibold"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ─── The hardware modal ───────────────────────────────────────────── */}
      {hardwareEdit !== null && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div
            className="bg-card border border-border rounded-2xl max-w-md w-full p-5 space-y-3"
            data-hardware-modal
          >
            <h3 className="font-bold text-sm">
              {hardwareEdit.row === null ? <T>Pridať hardvér</T> : <T>Upraviť hardvér</T>}
            </h3>
            <label className="block space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <T>Popis</T>
              </span>
              <input
                data-hardware-description
                value={hardwareEdit.draft.description}
                onChange={(event) =>
                  setHardwareEdit({
                    ...hardwareEdit,
                    draft: { ...hardwareEdit.draft, description: event.target.value },
                  })
                }
                placeholder={tt('Fiskálna tlačiareň N5')}
                className={FIELD}
              />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <MoneyField
                label={tt('€/mes. (účtujeme)')}
                hint={tt('Musí byť vyššie než nula.')}
                testId="hardware-price-input"
                value={hardwareEdit.draft.monthlyPrice}
                onChange={(monthlyPrice) =>
                  setHardwareEdit({ ...hardwareEdit, draft: { ...hardwareEdit.draft, monthlyPrice } })
                }
              />
              <MoneyField
                label={tt('€/mes. (náklad)')}
                hint={tt('Nula je v poriadku - neúčtuje sa, len sa z nej počíta marža.')}
                testId="hardware-cost-input"
                value={hardwareEdit.draft.monthlyCost}
                onChange={(monthlyCost) =>
                  setHardwareEdit({ ...hardwareEdit, draft: { ...hardwareEdit.draft, monthlyCost } })
                }
              />
            </div>
            {/* §3.120 - the rental period. Until this existed a device could be recorded and never
                returned: `BillingRunner` bills on `starts_at`/`ends_at` and the only lever here was
                *Aktívne*, which is period-blind and takes the device off earlier periods too. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-hardware-period>
              <DateField
                label={tt('Od')}
                hint={tt('Účtuje sa od tohto dňa.')}
                testId="hardware-starts-input"
                value={hardwareEdit.draft.startsAt ?? ''}
                onChange={(startsAt) =>
                  setHardwareEdit({ ...hardwareEdit, draft: { ...hardwareEdit.draft, startsAt } })
                }
              />
              <DateField
                label={tt('Do (vrátené)')}
                hint={tt('Prázdne znamená, že zariadenie je stále u franšízy.')}
                testId="hardware-ends-input"
                value={hardwareEdit.draft.endsAt ?? ''}
                onChange={(endsAt) =>
                  setHardwareEdit({ ...hardwareEdit, draft: { ...hardwareEdit.draft, endsAt } })
                }
              />
            </div>
            <EnabledToggle
              testId="hardware-enabled"
              checked={hardwareEdit.draft.enabled}
              onChange={(enabled) =>
                setHardwareEdit({ ...hardwareEdit, draft: { ...hardwareEdit.draft, enabled } })
              }
            />
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                data-hardware-cancel
                onClick={() => setHardwareEdit(null)}
                className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-hardware-save
                disabled={busy}
                onClick={saveHardware}
                className="min-h-[36px] px-3 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
              >
                <T>Uložiť</T>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── The module modal ─────────────────────────────────────────────── */}
      {moduleEdit !== null && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div
            className="bg-card border border-border rounded-2xl max-w-md w-full p-5 space-y-3"
            data-module-modal
          >
            <h3 className="font-bold text-sm">
              {moduleEdit.row === null ? <T>Pridať účtovanie modulu</T> : <T>Upraviť účtovanie</T>}
            </h3>
            <label className="block space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <T>Modul</T>
              </span>
              {/* **Only modules not already charged, when adding.** `addModuleBilling` upserts on
               * (franchise_id, module_key), so offering a charged key would make *Pridať* overwrite
               * a row already on the invoice. Editing offers all of them, or the form would open
               * with its own module missing from the list. */}
              <select
                data-module-key
                disabled={moduleEdit.row !== null}
                value={moduleEdit.draft.moduleKey ?? ''}
                onChange={(event) =>
                  setModuleEdit({
                    ...moduleEdit,
                    draft: { ...moduleEdit.draft, moduleKey: event.target.value },
                  })
                }
                className={FIELD}
              >
                {offered.map((module) => (
                  <option key={module.key} value={module.key}>
                    {module.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <T>Popis na faktúre</T>
              </span>
              <input
                data-module-description
                value={moduleEdit.draft.description}
                onChange={(event) =>
                  setModuleEdit({
                    ...moduleEdit,
                    draft: { ...moduleEdit.draft, description: event.target.value },
                  })
                }
                className={FIELD}
              />
              <span className="block text-[10px] text-muted-foreground">
                <T>Nepovinné - bez popisu sa použije názov modulu.</T>
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <MoneyField
                label={tt('€/mes. (účtujeme)')}
                hint={tt('Musí byť vyššie než nula.')}
                testId="module-price-input"
                value={moduleEdit.draft.monthlyPrice}
                onChange={(monthlyPrice) =>
                  setModuleEdit({ ...moduleEdit, draft: { ...moduleEdit.draft, monthlyPrice } })
                }
              />
              <MoneyField
                label={tt('€/mes. (náklad)')}
                hint={tt('Nula je v poriadku.')}
                testId="module-cost-input"
                value={moduleEdit.draft.monthlyCost}
                onChange={(monthlyCost) =>
                  setModuleEdit({ ...moduleEdit, draft: { ...moduleEdit.draft, monthlyCost } })
                }
              />
            </div>
            <EnabledToggle
              testId="module-enabled"
              checked={moduleEdit.draft.enabled}
              onChange={(enabled) =>
                setModuleEdit({ ...moduleEdit, draft: { ...moduleEdit.draft, enabled } })
              }
            />
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                data-module-cancel
                onClick={() => setModuleEdit(null)}
                className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-module-save
                disabled={busy}
                onClick={saveModule}
                className="min-h-[36px] px-3 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
              >
                <T>Uložiť</T>
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={tt('Zrušiť účtovanie?')}
        description={
          removing === null
            ? ''
            : tt(
                'Riadok sa odstráni a od nasledujúcej faktúry sa neúčtuje. Vystavené faktúry sa nemenia.',
              )
        }
        confirmLabel={tt('Odstrániť')}
        icon={<Trash2 className="w-5 h-5" />}
        loading={dropHardware.isPending || dropModule.isPending}
        onConfirm={() => {
          if (removing === null) return
          if (removing.kind === 'hardware') {
            dropHardware.mutate({ id: removing.id })
            return
          }
          dropModule.mutate({ id: removing.id })
        }}
      />
    </>
  )
}
